import React from "react";
import { Box } from "@chakra-ui/react";
import { languageOptions } from "./theme/themeOptions";
import { manaCodeBlockThemeLight } from "./theme/mana";
import IconButton from "../IconButton/IconButton";
import { FALCopy } from "../../icons/FontAwesomeIcons";
import { EditorView, lineNumbers, ViewUpdate } from "@codemirror/view";
import { EditorState, Compartment } from "@codemirror/state";
import { foldGutter, StreamLanguage } from "@codemirror/language";
import { css } from "@codemirror/lang-css";
import { html } from "@codemirror/lang-html";
import { markdown } from "@codemirror/lang-markdown";
import { java } from "@codemirror/lang-java";
import { javascript } from "@codemirror/lang-javascript";
import { json } from "@codemirror/lang-json";
import { shell } from "@codemirror/legacy-modes/mode/shell";
import { linter, lintGutter, Diagnostic } from "@codemirror/lint";
import { history, defaultKeymap, historyKeymap } from "@codemirror/commands";
import { keymap } from "@codemirror/view";
import { useNonce } from "../ManaUIProvider/NonceProvider";
import { useToast } from "../Toast/hooks";
import { useTranslation } from "../ManaUIProvider";
import defaultJsonValidate from "./validators/json";
import { fasTriangleExclamation, fasCircleExclamation, falCircleExclamation } from "../../icons/fortawesome";

export type { Diagnostic } from "@codemirror/lint";

/**
 * Helper function to convert icon data to a CSS-compatible data URI SVG string
 */
const iconToDataUri = (icon: { viewBox?: string; d?: string }, fill: string = "currentColor") => {
  if (!icon.viewBox || !icon.d) {
    throw new Error("Icon must have viewBox and d properties");
  }
  const [minX, minY, width, height] = icon.viewBox.split(",").map((v) => v.trim());
  const svgContent = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${minX} ${minY} ${width} ${height}"><path fill="${fill}" d="${icon.d}"/></svg>`;
  return `url('data:image/svg+xml,${encodeURIComponent(svgContent)}')`;
};

export interface CodeBlockProps {
  code: string;
  copyLabel?: string;
  language: keyof typeof languageOptions;
  hideLineNumbers?: boolean;
  disableCodeFolding?: boolean;
  disableMaxHeight?: boolean;
  enableLineWrapping?: boolean;
  editable?: boolean;
  onCodeChange?: (codeLines: string[]) => void;
  onCursorChange?: (cursorPosition: number) => void;
  lint?: boolean;
  validate?: (code: string) => Diagnostic[];
  onLint?: (diagnostics: Diagnostic[]) => void;
}

const getLanguageHighlighting = (lang: keyof typeof languageOptions) => {
  switch (lang) {
    case "css":
      return css();
    case "shell":
      return StreamLanguage.define(shell);
    case "html":
      return html();
    case "java":
      return java();
    case "javascript":
      return javascript();
    case "json":
      return json();
    case "jsx":
      return javascript({ jsx: true });
    case "markdown":
      return markdown();
    case "typescript":
      return javascript({ typescript: true });
    case "tsx":
      return javascript({ typescript: true, jsx: true });
    default:
      return javascript();
  }
};

export default function CodeBlock(props: CodeBlockProps) {
  const {
    code,
    language,
    hideLineNumbers,
    disableCodeFolding,
    disableMaxHeight,
    enableLineWrapping,
    copyLabel,
    editable,
    onCodeChange,
    onCursorChange,
    lint,
    validate,
    onLint,
    ...rest
  } = props;

  const nonce = useNonce();
  const { copyLabelDefault, confirmation } = useTranslation("CodeBlock");

  const codemirrorElem = React.useRef<HTMLDivElement | null>(null);
  const languageCompartmentRef = React.useRef<Compartment | null>(null);
  const lintCompartmentRef = React.useRef<Compartment | null>(null);
  const editableCompartmentRef = React.useRef<Compartment | null>(null);
  const toast = useToast();
  const [editorView, setEditorView] = React.useState<EditorView | null>(null);
  const [diagnostics, setDiagnostics] = React.useState<Diagnostic[]>([]);
  const setDiagnosticsRef = React.useRef<typeof setDiagnostics | null>(null);
  React.useEffect(() => {
    setDiagnosticsRef.current = setDiagnostics;
  }, [setDiagnostics]);
  // Ref to the diagnostics DOM element so we can measure its rendered height
  const diagnosticsRef = React.useRef<HTMLDivElement | null>(null);

  // Measure the diagnostics box height and set editor paddingBottom in pixels
  // so messages are fully visible.
  React.useEffect(() => {
    if (!editorView) {
      return;
    }
    try {
      const dom = editorView.dom as HTMLElement;
      if (!diagnostics || diagnostics.length === 0) {
        dom.style.paddingBottom = "";
        return;
      }

      // Wait for the diagnostics box to render and wrap (next frame)
      requestAnimationFrame(() => {
        try {
          const el = diagnosticsRef.current as HTMLElement | null;
          if (el) {
            const height = el.offsetHeight;
            dom.style.paddingBottom = `${height + 4}px`;
          } else {
            // fallback
            dom.style.paddingBottom = `48px`;
          }
        } catch (e) {
          /* ignore measurement errors */
        }
      });
    } catch (e) {
      /* ignore */
    }
  }, [editorView, diagnostics]);

  // Stabilize the callback functions to prevent re-initializing the editor
  const onCodeChangeRef = React.useRef(onCodeChange);
  React.useEffect(() => {
    onCodeChangeRef.current = onCodeChange;
  }, [onCodeChange]);

  const onCursorChangeRef = React.useRef(onCursorChange);
  React.useEffect(() => {
    onCursorChangeRef.current = onCursorChange;
  }, [onCursorChange]);

  const validateRef = React.useRef<typeof validate | undefined>(validate);
  React.useEffect(() => {
    validateRef.current = validate;
  }, [validate]);

  const onLintRef = React.useRef<typeof onLint | undefined>(onLint);
  React.useEffect(() => {
    onLintRef.current = onLint;
  }, [onLint]);

  const onCopy = () => {
    toast({
      ...confirmation,
      status: "success",
    });
    navigator.clipboard.writeText(code);
  };

  const customEditorStyles = React.useMemo(() => {
    // Convert FontAwesome icons to data URI SVG strings for CSS
    const warningIconSvg = iconToDataUri(fasTriangleExclamation, "#985C0C");
    const errorIconSvg = iconToDataUri(fasCircleExclamation, "#DC3545");

    const theme = EditorView.theme({
      "&": {
        padding: "1rem",
        paddingRight: "3rem",
        fontFamily: "var(--chakra-fonts-mono)",
        borderWidth: "var(--chakra-borderWidths-xs)",
        borderStyle: "solid",
        borderColor: "var(--chakra-colors-borders-neutral-soft-resting)",
        borderRadius: "var(--chakra-radii-sm)",
        ...(disableMaxHeight ? {} : { maxHeight: "37.5rem" }),
        ...(editable
          ? {
              "&:hover": {
                borderColor: "var(--chakra-colors-borders-primary-strong-hover)",
                boxShadow: "var(--chakra-shadows-gray)",
              },
            }
          : {}),
        "&[data-invalid=\"true\"]": {
          borderColor: "var(--chakra-colors-borders-danger-strong-resting)",
          boxShadow: "var(--chakra-shadows-error)",
        },
        "&:focus-within": {
          borderColor: "var(--chakra-colors-borders-primary-strong-active)",
          boxShadow:
            "var(--chakra-shadows-focus), inset 0 0 0 0.075rem var(--chakra-colors-borders-primary-strong-active)",
          outline: "none",
        },
      },
      ".cm-line": {
        padding: "0 1.25rem",
        lineHeight: "2",
      },
      ".cm-gutters": {
        borderRight: "none",
      },
      ".cm-gutter": {
        lineHeight: "2",
      },
      // ensure gutter elements (line numbers and lint markers) are centered vertically
      ".cm-gutters .cm-gutterElement, .cm-lint-gutter .cm-gutterElement": {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
      },
      // center lint markers in gutter
      ".cm-lint-gutter .cm-gutterElement": {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      },
      ".cm-lintMarker, .cm-lint-marker": {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
      },
      // Override default CodeMirror lint marker icons with custom SVGs
      ".cm-lint-marker": {
        width: "1.2em",
        height: "1.2em",
      },
      ".cm-lint-marker-warning": {
        content: warningIconSvg,
      },
      ".cm-lint-marker-error": {
        content: errorIconSvg,
      },
      // Hide lint underlines and tooltips since we show errors at the bottom
      ".cm-lintRange-error": {
        backgroundImage: "none",
      },
      ".cm-lintRange-warning": {
        backgroundImage: "none",
      },
      ".cm-lintRange-info": {
        backgroundImage: "none",
      },
      ".cm-lintRange-hint": {
        backgroundImage: "none",
      },
      ".cm-tooltip": {
        display: "none",
      },
      // when the editor is focused but has validation errors, show the error style
      // and ensure it overrides the normal focus style
      "&[data-invalid=\"true\"]:focus-within": {
        borderColor: "var(--chakra-colors-borders-danger-strong-resting)",
        boxShadow: "var(--chakra-shadows-error)",
        outline: "none",
      },
    });

    return [theme];
  }, [disableMaxHeight, editable]);

  const handleUpdate = (
    v: ViewUpdate,
    onCodeChange?: (lines: string[]) => void,
    onCursorChange?: (position: number) => void,
  ) => {
    if (onCodeChange && v.docChanged) {
      onCodeChange(v.state.doc.toJSON());
    }
    if (onCursorChange && (v.selectionSet || v.docChanged)) {
      const cursorPosition = v.state.selection.main.head;
      onCursorChange(cursorPosition);
    }
  };

  // Initialize compartments once
  if (!languageCompartmentRef.current) {
    languageCompartmentRef.current = new Compartment();
  }
  if (!lintCompartmentRef.current) {
    lintCompartmentRef.current = new Compartment();
  }
  if (!editableCompartmentRef.current) {
    editableCompartmentRef.current = new Compartment();
  }

  const buildLintExtension = () => {
    if (!(lint || validateRef.current)) {
      return [];
    }

    return [
      linter((view) => {
        const codeStr = view.state.doc.toString();
        const validateFn =
          validateRef.current ?? ((c: string) => {
            if (language === "json") {
              return defaultJsonValidate(c);
            }
            return [];
          });

        const diags = validateFn(codeStr) || [];
        const mapped: Diagnostic[] = diags.map((d) => ({
          from: d.from ?? 0,
          to: d.to ?? codeStr.length,
          message: d.message || "",
          severity: d.severity || "error",
        }));

        Promise.resolve().then(() => {
          try {
            if (view.dom) {
              view.dom.setAttribute("data-invalid", mapped.length > 0 ? "true" : "false");
            }
          } catch (e) {
            /* ignore */
          }
          // update local diagnostics state so we can render a persistent message
          try {
            if (setDiagnosticsRef.current) {
              setDiagnosticsRef.current(mapped);
            }
          } catch (e) {
            /* ignore */
          }
          if (onLintRef.current) {
            try {
              onLintRef.current(mapped);
            } catch (e) {
              /* ignore */
            }
          }
        });

        return mapped;
      }),
      lintGutter(),
    ];
  };

  // Create the EditorView once on mount (guarded)
  React.useEffect(() => {
    const view = new EditorView({
      state: EditorState.create({
        doc: code,
        extensions: [
          EditorView.cspNonce.of(nonce),
          editableCompartmentRef.current!.of(EditorView.editable.of(editable ?? false)),
          onCodeChangeRef.current || onCursorChangeRef.current
            ? EditorView.updateListener.of((v: ViewUpdate) =>
                handleUpdate(v, onCodeChangeRef.current, onCursorChangeRef.current),
              )
            : [],
          hideLineNumbers ? [] : lineNumbers(),
          disableCodeFolding ? [] : foldGutter(),
          enableLineWrapping ? EditorView.lineWrapping : [],
          manaCodeBlockThemeLight,
          customEditorStyles,
          languageCompartmentRef.current!.of(getLanguageHighlighting(language)),
          lintCompartmentRef.current!.of(buildLintExtension()),
          history(),
          keymap.of([...defaultKeymap, ...historyKeymap]),
        ],
      }),
      parent: codemirrorElem.current!,
    });

    // Add focusin/focusout handlers to apply inline styles when focused and invalid
    const dom = view.dom as HTMLElement;
    const applyFocusedInvalidVisual = () => {
      try {
        if (dom.getAttribute("data-invalid") === "true") {
          dom.style.outline = "var(--chakra-borderWidths-xs) solid var(--chakra-colors-borders-danger-strong-resting)";
        } else {
          dom.style.outline = "";
          dom.style.boxShadow = "";
        }
      } catch (e) {
        /* ignore */
      }
    };

    const onFocusIn = () => {
      applyFocusedInvalidVisual();
    };
    const onFocusOut = () => {
      try {
        // remove inline overrides so CSS can control the appearance
        dom.style.outline = "";
        dom.style.boxShadow = "";
      } catch (e) {
        /* ignore */
      }
    };

    // Watch for data-invalid attribute changes so the inline visual updates
    // immediately if the error is fixed while the editor is focused.
    const observer = new MutationObserver(() => {
      // Only update visuals when the editor has focus
      try {
        if (dom.contains(document.activeElement)) {
          applyFocusedInvalidVisual();
        }
      } catch (e) {
        /* ignore */
      }
    });
    observer.observe(dom, { attributes: true, attributeFilter: ["data-invalid"] });

    dom.addEventListener("focusin", onFocusIn);
    dom.addEventListener("focusout", onFocusOut);

    setEditorView(view);

    return () => {
      observer.disconnect();
      dom.removeEventListener("focusin", onFocusIn);
      dom.removeEventListener("focusout", onFocusOut);
      view.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nonce]);

  // Reconfigure compartments
  React.useEffect(() => {
    if (!editorView) {
      return;
    }
    editorView.dispatch({
      effects: languageCompartmentRef.current!.reconfigure(getLanguageHighlighting(language)),
    });
  }, [language, editorView]);

  React.useEffect(() => {
    if (!editorView) {
      return;
    }
    editorView.dispatch({
      effects: editableCompartmentRef.current!.reconfigure(EditorView.editable.of(editable ?? false)),
    });
  }, [editable, editorView]);

  React.useEffect(() => {
    if (!editorView) {
      return;
    }
    editorView.dispatch({
      effects: lintCompartmentRef.current!.reconfigure(buildLintExtension()),
    });
  }, [lint, validate, onLint, language, editorView]);

  // Keep the editor's internal state synchronized with the code prop
  React.useEffect(() => {
    if (editorView) {
      const editorDoc = editorView.state.doc.toString();
      if (code !== editorDoc) {
        const changes = getDiff(editorDoc, code);
        if (changes) {
          editorView.dispatch({ changes });
        }
      }
    }
  }, [code, editorView]);

  const errorMessageIcon = (() => {
    if (!falCircleExclamation.viewBox || !falCircleExclamation.d) {
      return null;
    }
    const [minX, minY, width, height] = falCircleExclamation.viewBox.split(",").map((v) => v.trim());
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox={`${minX} ${minY} ${width} ${height}`} fill="none">
        <path d={falCircleExclamation.d} fill="#DC3545"/>
      </svg>
    );
  })();

  return (
    <Box w="full" position="relative">
      {!editable && (
        <IconButton
          icon={<FALCopy />}
          aria-label={copyLabel ?? copyLabelDefault}
          tooltipLabel={copyLabel ?? copyLabelDefault}
          size="lg"
          variant="outline.soft"
          shape="round"
          position="absolute"
          zIndex={1}
          top={4}
          right={4}
          onClick={onCopy}
        />
      )}
      <div ref={codemirrorElem} {...rest} />
      {diagnostics && diagnostics.length > 0 && (
        <Box
          ref={diagnosticsRef}
          position="absolute"
          bottom="0.5rem"
          left="1.5rem"
          bg="transparent"
          color="var(--chakra-colors-borders-danger-strong-resting)"
          px={0}
          py={0}
          borderRadius="0"
          boxShadow="none"
          fontSize="sm"
          pointerEvents="none"
          aria-live="polite"
          data-testid="codeblock-diagnostics"
          display="flex"
          alignItems="center"
        >
          <span style={{ display: "inline-flex", marginRight: 8 }} aria-hidden>
            {errorMessageIcon}
          </span>
          <span style={{ paddingRight: 4 }}>{diagnostics[0].message}</span>
        </Box>
      )}
    </Box>
  );
}

/**
 * Calculates the minimal changes needed to get from an old string to a new one.
 */
const getDiff = (oldDoc: string, newDoc: string) => {
  if (oldDoc === newDoc) {
    return null;
  }

  let from = 0;
  while (
    from < oldDoc.length &&
    from < newDoc.length &&
    oldDoc[from] === newDoc[from]
  ) {
    from++;
  }

  let to = oldDoc.length;
  let end = newDoc.length;
  while (to > from && end > from && oldDoc[to - 1] === newDoc[end - 1]) {
    to--;
    end--;
  }

  const insert = newDoc.slice(from, end);
  return { from, to, insert };
};
